import { useState } from 'react';
import type { Contract, Farmer } from '../../../../types';
import { getLoansByFarmer, getDebtsByFarmer, getLimitsByFarmer, getContractsByFarmer, getSeasonsByFarmer, linkContractSeason } from '../../../../store';
import { StatusBadge } from '../../../../components/base/StatusBadge';
import { EmptyState } from '../../../../components/base/EmptyState';
import { MobileTabs } from '../../../../components/base/MobileUi';
import { formatDate, formatVND, formatCompactCurrency } from '../../../../utils/format';
import { useStore } from '../../../../hooks/useStore';
import { useAuth } from '../../../../hooks/useAuth';
import { ContractAttachments } from './ContractAttachments';
import { DEBT_STATUS, CONTRACT_STATUS } from '../../../../utils/status';

type FinanceSubTab = 'loans' | 'limits' | 'contracts';

function LinkSeasonControl({ contract, farmerId, htxId }: { contract: Contract; farmerId: string; htxId?: string }) {
  const seasons = getSeasonsByFarmer(farmerId);
  const available = seasons.filter((s) => !contract.seasonIds?.includes(s.id));
  const [seasonId, setSeasonId] = useState('');
  const [busy, setBusy] = useState(false);

  if (available.length === 0) return null;

  async function handleLink() {
    if (!seasonId) return;
    setBusy(true);
    try {
      await linkContractSeason(contract.id, seasonId, farmerId, htxId);
      setSeasonId('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex items-center gap-1.5 mt-2">
      <select
        value={seasonId}
        onChange={(e) => setSeasonId(e.target.value)}
        className="flex-1 h-7 px-1.5 rounded-md border border-slate-200 text-xs bg-white"
      >
        <option value="">— Gán mùa vụ vào hợp đồng —</option>
        {available.map((s) => (
          <option key={s.id} value={s.id}>{s.name}</option>
        ))}
      </select>
      <button
        type="button"
        disabled={!seasonId || busy}
        onClick={handleLink}
        className="h-7 px-2 rounded-md bg-agrida-700 text-white text-xs font-semibold disabled:opacity-50"
      >
        Gán
      </button>
    </div>
  );
}

interface DetailFinanceTabProps {
  farmer: Farmer;
}

export default function DetailFinanceTab({ farmer }: DetailFinanceTabProps) {
  useStore();
  const { user } = useAuth();
  const [subTab, setSubTab] = useState<FinanceSubTab>('loans');
  const loans = getLoansByFarmer(farmer.id);
  const debts = getDebtsByFarmer(farmer.id);
  const limits = getLimitsByFarmer(farmer.id);
  const contracts = getContractsByFarmer(farmer.id);
  const seasons = getSeasonsByFarmer(farmer.id);
  void seasons;

  const totalNo = debts.filter((d) => d.conNo > 0).reduce((s, d) => s + d.conNo, 0);
  const noQuaHan = debts.filter((d) => d.status === DEBT_STATUS.OVERDUE).reduce((s, d) => s + d.conNo, 0);
  const hasOverdue = noQuaHan > 0;
  const hanMucTong = limits.reduce((s, l) => s + l.hanMuc, 0);
  const daDung = limits.reduce((s, l) => s + l.daSuDung, 0);
  const conLai = hanMucTong - daDung;
  const dangKhoa = limits.reduce((s, l) => s + l.dangKhoa, 0);
  const validContracts = contracts.filter((c) => c.status === CONTRACT_STATUS.ACTIVE);
  const expiringContracts = contracts.filter((c) => c.status === CONTRACT_STATUS.EXPIRING_SOON);
  const activeDebts = debts.filter((d) => d.conNo > 0);
  const usagePercent = hanMucTong > 0 ? (daDung / hanMucTong) * 100 : 0;
  const usageColor = usagePercent >= 90 ? 'bg-red-500' : usagePercent >= 70 ? 'bg-amber-500' : 'bg-agrida-500';

  return (
    <div className="space-y-5">
      {/* ── Summary cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 md:gap-3">
        <div className="bg-white rounded-xl border border-slate-200 p-3 text-center">
          <p className="text-xs text-slate-400">Tổng hạn mức</p>
          <p className="text-sm font-bold text-slate-900">{hanMucTong > 0 ? formatCompactCurrency(hanMucTong) : '—'}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-3 text-center">
          <p className="text-xs text-slate-400">Còn lại</p>
          <p className={`text-sm font-bold ${hanMucTong > 0 ? 'text-agrida-700' : 'text-slate-400'}`}>{hanMucTong > 0 ? formatCompactCurrency(conLai) : '—'}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-3 text-center">
          <p className="text-xs text-slate-400">Dư nợ</p>
          <p className={`text-sm font-bold ${totalNo > 0 ? 'text-red-600' : 'text-slate-400'}`}>{totalNo > 0 ? formatCompactCurrency(totalNo) : '0'}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-3 text-center">
          <p className="text-xs text-slate-400">HĐ hiệu lực</p>
          <p className="text-sm font-bold text-sky-700">{validContracts.length}</p>
        </div>
      </div>

      {/* ── Usage progress ── */}
      {hanMucTong > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex justify-between text-xs text-slate-400 mb-1">
            <span>Đã dùng {formatCompactCurrency(daDung)} / {formatCompactCurrency(hanMucTong)}</span>
            <span>{usagePercent.toFixed(0)}%</span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all ${usageColor}`} style={{ width: `${Math.min(100, usagePercent)}%` }} />
          </div>
          {/* Locked capacity ≠ owed debt — see the matching note on the sheet
              header. Spelled out again here since this tab is where "Còn lại"
              and "Dư nợ" sit far enough apart to otherwise look contradictory. */}
          {dangKhoa > 0 && (
            <p className="mt-2 flex items-start gap-1 text-[11px] text-amber-700">
              <i className="ri-lock-line mt-px shrink-0" />
              Trong đó {formatCompactCurrency(dangKhoa)} đang khóa cho khoản vay chưa giải ngân — chưa tính vào Dư nợ.
            </p>
          )}
        </div>
      )}

      {/* ── Sub-tabs ── */}
      <MobileTabs<FinanceSubTab>
        tabs={[
          { id: 'loans', label: `Lịch sử vay (${loans.length})` },
          { id: 'limits', label: `Hạn mức (${limits.length})` },
          { id: 'contracts', label: `Hợp đồng (${contracts.length})` },
        ]}
        value={subTab}
        onChange={setSubTab}
        equal
      />

      {subTab === 'contracts' && (
        <div>
          {validContracts.length === 0 && expiringContracts.length === 0 && contracts.length === 0 ? (
            <EmptyState icon="ri-file-text-line" title="Chưa có hợp đồng" description="Nông hộ chưa có hợp đồng bao tiêu nào." />
          ) : (
            <div className="space-y-3">
              {[...validContracts, ...expiringContracts, ...contracts.filter((c) => c.status !== CONTRACT_STATUS.ACTIVE && c.status !== CONTRACT_STATUS.EXPIRING_SOON)].map((c) => (
                <div key={c.id} className="bg-white rounded-xl border border-slate-200 p-3 md:p-4">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <p className="text-sm font-semibold text-slate-900">{c.soHopDong}</p>
                    <StatusBadge status={c.status} size="sm" />
                  </div>
                  <p className="text-xs text-slate-500">{c.loaiHopDong} · {c.doiTac}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs">
                    <span className="text-slate-600">Giá trị: <span className="font-medium">{formatVND(c.giaTri)}</span></span>
                    <span className="text-slate-400">{formatDate(c.ngayHieuLuc || c.ngayKy)} - {formatDate(c.ngayHetHan)}</span>
                    {c.cayTrong && <span className="text-slate-500">{c.cayTrong} ({c.dienTich}ha)</span>}
                  </div>
                  {user?.role === 'Hợp tác xã' && farmer.membershipStatus === 'Đã xác nhận' && (
                    <LinkSeasonControl contract={c} farmerId={farmer.id} htxId={user?.htxId} />
                  )}
                  <ContractAttachments contract={c} farmerId={farmer.id} htxId={user?.htxId} canEdit={user?.role === 'Hợp tác xã'} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {subTab === 'limits' && (
        <div>
          {limits.length === 0 ? (
            <EmptyState icon="ri-scales-3-line" title="Chưa có hạn mức" description="Nông hộ chưa được cấp hạn mức nào." />
          ) : (
            <div className="space-y-2">
              {limits.map((l) => (
                <div key={l.id} className="bg-white rounded-xl border border-slate-200 p-3">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-medium text-slate-800">{l.loaiDinhMuc}</p>
                    <div className="w-20 h-2 bg-slate-200 rounded-full overflow-hidden shrink-0">
                      <div className="h-full bg-agrida-600 rounded-full" style={{ width: `${Math.min(100, (l.daSuDung / l.hanMuc) * 100)}%` }} />
                    </div>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    Hạn mức: {formatCompactCurrency(l.hanMuc)} · Đã dùng: {formatCompactCurrency(l.daSuDungThucTe ?? l.daSuDung)}
                    {!!l.dangKhoa && ` · Đang khóa: ${formatCompactCurrency(l.dangKhoa)}`} · Còn: {formatCompactCurrency(l.hanMuc - l.daSuDung)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {subTab === 'loans' && (
        <div className="space-y-5">
          <div>
            <h3 className="text-sm font-semibold text-slate-900 mb-2">Lịch sử vay ({loans.length})</h3>
            {loans.length === 0 ? (
              <EmptyState icon="ri-money-cny-circle-line" title="Chưa có yêu cầu" description="Nông hộ chưa có yêu cầu tạm ứng nào." />
            ) : (
              <div className="space-y-2">
                {loans.map((l) => (
                  <div key={l.id} className="bg-white rounded-xl border border-slate-200 p-3">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <p className="text-xs font-semibold text-slate-900">
                        {l.maKhoanVay || `#${l.id.slice(-6).toUpperCase()}`}
                        <span className="font-normal text-slate-500"> · {l.mucDich || (l.loai === 'Vật tư' ? 'Vay vật tư' : 'Vay tiền')}</span>
                      </p>
                      <StatusBadge status={l.status} size="sm" />
                      {l.isOverLimit && <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">Vượt HM</span>}
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
                      <span className="text-slate-600">{formatVND(l.soTien)}</span>
                      <span className="text-slate-400">{formatDate(l.ngayYeuCau)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <h3 className="text-sm font-semibold text-slate-900 mb-2">
              Dư nợ ({activeDebts.length})
              {hasOverdue && <span className="ml-2 text-xs font-normal text-red-600">— có {debts.filter((d) => d.status === DEBT_STATUS.OVERDUE).length} khoản quá hạn</span>}
            </h3>
            {activeDebts.length === 0 ? (
              <EmptyState icon="ri-file-list-3-line" title="Không có dư nợ" description="Nông hộ không có khoản nợ nào đang hoạt động." />
            ) : (
              <div className="space-y-2">
                {activeDebts.map((d) => (
                  <div key={d.id} className={`bg-white rounded-xl border p-3 ${d.status === DEBT_STATUS.OVERDUE ? 'border-red-200 bg-red-50/30' : 'border-slate-200'}`}>
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className="text-sm font-semibold text-slate-900">{d.maKhoanVay || d.id}</p>
                      <StatusBadge status={d.status} size="sm" />
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-0.5 text-xs">
                      <div><span className="text-slate-400">Tổng nợ:</span> <span className="font-medium text-slate-700">{formatVND(d.soTien)}</span></div>
                      <div><span className="text-slate-400">Đã trả:</span> <span className="font-medium text-agrida-700">{formatVND(d.daTra)}</span></div>
                      <div><span className="text-slate-400">Còn nợ:</span> <span className={`font-medium ${d.conNo > 0 ? 'text-red-600' : 'text-agrida-700'}`}>{formatVND(d.conNo)}</span></div>
                      <div><span className="text-slate-400">Đến hạn:</span> <span className="font-medium text-slate-700">{formatDate(d.ngayDenHan)}</span></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}